import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { checkServiceability, validateAddressServiceability, resetState, saveBuyflow, saveServiceCheckId, serviceCheck, sessionCall, setTFN, updateIsBusinessAddress, updateOrder, clearNormalizedAddress, saveSpanishLanguagePreference, saveInternetPlan, saveFirstStep } from '../../store/action/OrderAction';
import queryString from 'query-string';
import { useCallCenterFlag, useLoading, useServiceablitityData, useSessionData, useSinglePageCheckoutNow } from '../../hooks/order';
import { routesConfig, OrderStatus } from '../../util/util';
import {
  getSegregatedAddress,
  setCjevent,
  isUndefinedNullOrEmpty,
  getAggregatedAddress,
  getBaseUrl,
  filterFFPlans,
  filterFFFixedWireless,
} from '../../common/common';
import { useFFReferral } from '../../hooks/useFFReferralSession';
import { datalayerServiceabilityCheckCompleted } from '../../common/GTMScripts';
import { getCheckServiceableModel, getServiceCheckModel } from '../../helpers/OrderHelperFunctions';
import { filterFixedWirelessProducts } from '../../helpers/tfn';
import { getDefaultPlan } from '../../helpers/defaultPlan';
import { BackdropLoader, Loader } from '../common/Loader';
import AddressSuggestions from './AddressSuggestions';
import AddressSuggestionSelect from '../common/AddressSuggestionSelect';
import { Checkbox, Row, Col } from 'antd';
import VerifyAddressModal from './VerifyAddressModal';
import EnterManualAddressModal from './EnterManualAddressModal';
import useLeadsSDK from '../../hooks/useLeadsSDK';
import { appendInvocaTestModeParam } from '../../util/invocaTestMode';
import { useTranslation } from 'react-i18next';
import { detectLangFromSearch, hasManualLangPreference } from '../../util/languageDetect';


const ServiceabilityCheck = () => {
  const { t } = useTranslation();
  const leads = useLeadsSDK();
  const location = useLocation();
  const navigate = useNavigate();
  const isSinglePageCheckoutNow = useSinglePageCheckoutNow();
  const { isInviteEarthlink } = useCallCenterFlag();
  const dispatch = useDispatch();
  const { sessionResponse, orderInfo, referrer } = useSessionData();
  const { ffPromoCode, isFFFlow } = useFFReferral();
  const { normalizedAddress, callLogId } = useServiceablitityData();
  const {
    isBusiness = false,
    addr = '',
    isMobile = false,
    isInternet = false,
    cjevent = '',
    leadId = '',
  } = queryString.parse(location.search);
  const [initialAddress, changeAddress] = useState(getSegregatedAddress(addr));
  const gid = (sessionResponse && sessionResponse.uid) || queryString.parse(location.search).gid;
  const referrerUri = (referrer) || queryString.parse(location.search).referrer;
  const { agentId } = sessionResponse;
  const cjeve = (sessionResponse && sessionResponse.cjevent) || cjevent;
  const [suggestedAddr, setsuggestedAddresses] = useState('');
  const [searchAddress, setSearchAddress] = useState(addr);
  const [currentDisplayAddress, setCurrentDisplayAddress] = useState('');
  const [loadingState, setLoading] = useState(true);
  const [validAddr, setValidAddr] = useState(false);
  const [addressFlag, setAddressFlag] = useState(false);
  const [showManualAddressModal, setShowManualAddressModal] = useState(false);
  const pendingSubmitRef = React.useRef(null);
  const [businessAddress, setBusinessAddr] = useState(isBusiness ? isBusiness === 'true' : isBusiness);
  const [internetAddress, setInternetAddr] = useState(isInternet ? isInternet === 'true' : isInternet);
  const [mobileAddress] = useState(isMobile ? isMobile === 'true' : isMobile);
  const hasAddrQueryParam = !isUndefinedNullOrEmpty(addr);

  const triggerServiceabilityEvent = ({
    address = '',
    products = {},
  } = {}) => {
    datalayerServiceabilityCheckCompleted({
      addr: address || searchAddress || '',
      gid: gid || '',
      isAddressChanged: !hasAddrQueryParam,
      products,
      referrer: referrerUri || '',
    });
  };

  useEffect(() => {
    if(hasManualLangPreference()){return}
    dispatch(saveSpanishLanguagePreference(detectLangFromSearch(location.search) === 'es'));
  }, []);

  useEffect(() => {
    if (Object.keys(sessionResponse)?.length === 0 && !isUndefinedNullOrEmpty(gid)) {
      dispatch(sessionCall(gid)).catch(() => {
        window.location.href = appendInvocaTestModeParam(getBaseUrl());
      })
    }
    if (cjeve) {
      setCjevent(cjeve);
    }
    setLoading(false)
    dispatch(setTFN('default'))
    // if (referrerUri) {
    //   dispatch(setCustomTFN(serviceTFN));
    //   dispatch(setCustomWhiTFN(whiServiceTFN));
    // }
  }, [Object.keys(sessionResponse).length]);

  useEffect(() => {
    if (addr && orderInfo && Object.keys(orderInfo).length) {
      handleSubmit(addr)
    } else {
      setInternetAddr(true)
    }
  }, [Object.keys(orderInfo).length])

  // ELNKWEB-3210: referrer traffic used to be sent off-domain to config.RESULTS_URL with the
  // TFN in the query string. The requirements doc forbids handing the visitor to a
  // third-party or reseller site from here, and that applies to every entry path - so
  // referrer traffic now stays in the app and renders the results view like everyone else.
  const redirectToResults = () => {
    navigate(routesConfig.nonServiceable);
  }

  useEffect(() => {
    if (searchAddress) {
      dispatch(validateAddressServiceability({ 'address': searchAddress })).then(res => {
        if (res?.value?.data?.dpvFootnotes === 'H#' || res?.value?.data?.dpvMatchCode === 'D') {
          pendingSubmitRef.current = null;
          setAddressFlag(true);
        } else {
          const pending = pendingSubmitRef.current;
          pendingSubmitRef.current = null;
          if (pending) {
            handleSubmit(pending.searchAddr, false, pending.addressObject);
          }
        }
      }).catch(() => {
        setAddressFlag(false);
        const pending = pendingSubmitRef.current;
        pendingSubmitRef.current = null;
        if (pending) {
          handleSubmit(pending.searchAddr, false, pending.addressObject);
        }
      })
    }
  }, [searchAddress]);

  const resetClick = () => {
    dispatch(resetState());
  }

  const handleNotSeen = () => {
    redirectToResults();
  }

  const handleSubmit = (searchAddr = searchAddress, suggestedAddress = false, selectedAddressObject = null) => {
    const orderAccessToken = orderInfo?.orderAccessToken || '';
    const id = sessionResponse && sessionResponse.id ? sessionResponse.id : null;
    // /prequal prices from the promo code it is given, so this read IS the discount. It comes
    // from redux, written only by a verified redemption - never from sessionStorage, where a
    // single setItem('promoCode', 'FRIENDSFAMILY26') would have bought real Friends & Family
    // pricing on an order carrying no token for any backend check to catch. Everything else
    // falls back to the server-issued session value, exactly as on master.
    const promoCode = ffPromoCode || (!isUndefinedNullOrEmpty(sessionResponse.promoCode) ? sessionResponse.promoCode : null);
    const businessPromoCode = sessionResponse && sessionResponse.businessPromoCode ? sessionResponse.businessPromoCode : null;
    const partnerId = sessionResponse && sessionResponse.partnerId ? sessionResponse.partnerId : null;
    const { address1 = '', address2 = '', city = '', state = '', zip = '' } = selectedAddressObject || initialAddress;
    const trackedAddress = suggestedAddress && typeof suggestedAddress === 'object'
      ? getAggregatedAddress(suggestedAddress)
      : (searchAddr || searchAddress || '');
    if (isUndefinedNullOrEmpty(orderAccessToken)) {
      return;
    }

    /**
     * Initialize the Lead
     */

    if (!isUndefinedNullOrEmpty(leadId)) {
      leads?.initLeadFromId?.(leadId);
    } else if (!isUndefinedNullOrEmpty(addr)) {
      leads?.initLeadFromAddress?.(address1, address2, city, state, zip);
    }

    if (!isUndefinedNullOrEmpty(id)) {
      const checkServiceabilityModel = getCheckServiceableModel(searchAddr, suggestedAddress, businessAddress, promoCode, partnerId, businessPromoCode);
      let serviceCheckmodel = getServiceCheckModel(searchAddr, address1, address2, city, state, zip, agentId, null);
      dispatch(updateIsBusinessAddress(businessAddress));
      // ELNKWEB-3210: drop the previous check's normalized address before starting a new
      // one. It is only rewritten on success, so without this a failed check would
      // leave the results view showing the address the customer just replaced.
      dispatch(clearNormalizedAddress());
      dispatch(checkServiceability(checkServiceabilityModel))
        .then(async (res) => {
          const { data } = res.value
          const { products, suggestedAddresses, nbcScore, mobileProducts, fixedWirelessProducts: rawFixedWirelessProducts } = res.value.data;
          // Filter once, at the source. The products page only renders the
          // filtered list, so every routing decision below has to agree with it -
          // otherwise a suppressed-only address is routed to a page that renders
          // nothing, or has a suppressed product auto-added to the cart.
          const fixedWirelessProducts =
            filterFixedWirelessProducts(rawFixedWirelessProducts, data.fixedWireless) || [];
          triggerServiceabilityEvent({
            address: trackedAddress,
            products: { fixedWirelessProducts, mobileProducts, products },
          });
          if (mobileAddress === true && internetAddress === false && mobileProducts?.length === 0) {
            redirectToResults(suggestedAddress ? getAggregatedAddress(suggestedAddress) : searchAddress);
            return;
          } else if ((mobileAddress === true && internetAddress === true) || (mobileAddress === true && businessAddress === true) || (!mobileAddress === true && internetAddress === false && businessAddress === false)) {
            dispatch(saveBuyflow('all'))
          } else if (internetAddress === true || businessAddress === true) {
            if (products.length > 0 || fixedWirelessProducts.length > 0) {
              dispatch(saveBuyflow('internet'))
            } else {
              dispatch(saveBuyflow('otherMobile'))
            }
          } else if (mobileAddress === true && mobileProducts.length > 0) {
            if (mobileProducts.length > 0) {
              dispatch(saveBuyflow('wireless'))
            } else {
              dispatch(saveBuyflow('otherInt'))
            }
          }
          serviceCheckmodel.nbcScore = nbcScore;
          if (suggestedAddresses.length && !products.length && !fixedWirelessProducts.length && !mobileProducts.length ) {
            setsuggestedAddresses(suggestedAddresses);
            setLoading(false);
          } else if (products.length || mobileProducts.length || fixedWirelessProducts.length) {
            serviceCheckmodel.productList = products;
            dispatch(serviceCheck(serviceCheckmodel)).then((response) => {
              dispatch(saveServiceCheckId(response.value.data.item.id))
              dispatch(updateOrder({
                billingAddress: '',
                nbcScore,
                orderStatus: OrderStatus.serviceCheck,
                serviceCheckId: response.value.data.item.id,
                serviceAddress: getAggregatedAddress(data.normalizedAddress),
                serviceAdd1: data.normalizedAddress.address1,
                serviceAdd2: data.normalizedAddress.address2,
                serviceCity: data.normalizedAddress.city,
                serviceState: data.normalizedAddress.state,
                serviceZip: data.normalizedAddress.zipCode,
                callLogId: data.callLogId,
              }, orderAccessToken));
            }).then(() => {
              setLoading(false);

              /**
               * Check address serviceability.
               */
              leads?.addressServiceable?.('Check Serviceability', {
                address1: data?.normalizedAddress?.address1,
                address2: data?.normalizedAddress?.address2,
                city: data?.normalizedAddress?.city,
                data: {
                  adslShippingCost: data?.adslShippingCost,
                  callLogId: data?.callLogId,
                  coverageStrength: {
                    fixedWireless:
                      data?.fixedWireless?.coverageStrength ?? 'N/A',
                    mobile: data?.mobile?.coverageStrength ?? 'N/A',
                  },
                  extendedInfo: {
                    serviceableService:
                      data?.extendedInfo?.serviceableService?.map((i) => i) ??
                      [],
                  },
                  nbcScore: data?.nbcScore,
                  products: {
                    fixedWireless: data?.fixedWirelessProducts?.map((i) => ({
                      id: String(i?.servRef),
                      name: i?.serviceName,
                    })),
                    mobile: data?.mobileProducts?.map((i) => ({
                      id: String(i?.servRef),
                      name: i?.serviceName,
                    })),
                    internet: data?.products?.map((i) => ({
                      id: String(i?.servRef),
                      name: i?.serviceName,
                    })),
                    vas: data?.vasProducts?.map((i) => ({
                      id: String(i?.servRef),
                      name: i?.serviceName,
                    })),
                  },
                },
                state: data?.normalizedAddress?.state,
                zip: data?.normalizedAddress?.zipCode,
                serviceable: true,
              });

              /**
               * Update order service address.
               */
              leads?.updateOrder?.(
                'Update Order: Serviceability Details',
                {
                  account: {
                    serviceAddress: {
                      address1: data?.normalizedAddress?.address1,
                      address2: data?.normalizedAddress?.address2,
                      city: data?.normalizedAddress?.city,
                      state: data?.normalizedAddress?.state,
                      zip: data?.normalizedAddress?.zipCode,
                      serviceable: 'Y',
                    },
                  },
                  data: {
                    promoCode,
                  }
                }
              );
            }).catch(() => setLoading(false));

            // invite.earthlink.net keeps the buy flow at every hour except at a 5G-only address,
            // which gets the 3-card call view on /products (ELNKWEB-3210), as it does today.
            // Everyone else with the buy flow, halo included, goes to
            // the single-page checkout with the 5G plan selected. "No plans" matches the products
            // page, whose plan list keeps only products with a speed.
            const hasSpeedPlans = Boolean(products?.some((plan) => plan?.downstreamSpd));
            const isFiveGOnlyCallView = !hasSpeedPlans && fixedWirelessProducts.length > 0 && isInviteEarthlink;
            if (isSinglePageCheckoutNow() && !businessAddress && !isFiveGOnlyCallView) {
              // Single page checkout skips /products entirely, so pre-select a plan from the
              // eligible ones (same narrowing Product.js applies) rather than landing on an
              // empty cart - X4Y discount first, then 300 Mbps, then lowest price (see
              // getDefaultPlan). The product section lets the customer change it from there.
              const eligiblePlans = isFFFlow ? filterFFPlans(products, data.serviceTerm) : products;
              const eligibleFixedWireless = isFFFlow
                ? filterFFFixedWireless(fixedWirelessProducts, data.serviceTerm)
                : fixedWirelessProducts;
              const defaultPlan = getDefaultPlan([...(eligiblePlans || []), ...(eligibleFixedWireless || [])], data.serviceTerm)
                || mobileProducts?.[0] || null;
              if (defaultPlan) {
                dispatch(saveInternetPlan(defaultPlan));
              }
              dispatch(saveFirstStep(true));
              navigate(routesConfig.checkout);
            } else {
              navigate(routesConfig.products);
            }
          } else {
            await dispatch(serviceCheck(serviceCheckmodel)).then(async (response) => {
              dispatch(updateOrder({
                billingAddress: '',
                nbcScore,
                orderStatus: OrderStatus.unserviceable,
                serviceCheckId: response.value.data.item.id,
                serviceAddress: getAggregatedAddress(data.normalizedAddress),
                serviceAdd1: data.normalizedAddress.address1,
                serviceAdd2: data.normalizedAddress.address2,
                serviceCity: data.normalizedAddress.city,
                serviceState: data.normalizedAddress.state,
                serviceZip: data.normalizedAddress.zipCode,
                callLogId: data.callLogId,
              }, orderAccessToken)).then(() => {
                setLoading(false)
                leads?.setDispositionClosed?.('Close Lead: Not Serviceable', {
                  data: {
                    reason: 'Not Serviceable',
                  },
                });
              }).catch(() => setLoading(false))
            });
            navigate(routesConfig.nonServiceable);
          }
        }).catch(async () => {
          triggerServiceabilityEvent({
            address: trackedAddress,
          });
          await dispatch(serviceCheck(serviceCheckmodel)).then(async (response) => {
            dispatch(updateOrder({
              billingAddress: '',
              orderStatus: OrderStatus.unserviceable,
              serviceCheckId: response.value.data.item.id,
              serviceAddress: getAggregatedAddress(normalizedAddress),
              serviceAdd1: normalizedAddress.address1,
              serviceAdd2: normalizedAddress.address2,
              serviceCity: normalizedAddress.city,
              serviceState: normalizedAddress.state,
              serviceZip: normalizedAddress.zipCode,
              callLogId,
            }, orderAccessToken)).then(() => {
              setLoading(false)
              leads?.setDispositionClosed?.('Close Lead: Not Serviceable', {
                data: {
                  reason: 'Not Serviceable',
                },
              });
            }).catch(() => setLoading(false))
          });
          redirectToResults(searchAddress);
        });
    } else {
      redirectToResults(searchAddress);
    }
  }

  const setAddress = (getSearchAddress, addressObject) => {
    setSearchAddress(getSearchAddress);
    setCurrentDisplayAddress(getSearchAddress);
    setValidAddr(true);
    changeAddress(addressObject);
    pendingSubmitRef.current = { searchAddr: getSearchAddress, addressObject };
  }

  const { loading } = useLoading();

  const handleClose = () => {
    setAddressFlag(false);
  };

  if (suggestedAddr.length && Object.keys(sessionResponse).length) {
    return (
      <AddressSuggestions
        onNotSeenClick={handleNotSeen}
        onChangeAddressClick={resetClick}
        onAddressClick={handleSubmit}
        className="userInput"
      />
    )
  }
  if (!queryString.parse(location.search).addr && Object.keys(sessionResponse).length) {
    return (
      <>
        <section className="serviceabilityCheck-section">
          <div className="elink-container mt-5 serviceabilityCheck address-page">
            <h1 className="elnk-internet-heading__title address-page-heading">
              {t('serviceability.heading')}
            </h1>
            <div className="card">
              <div className="inner-card layout-card">
                <VerifyAddressModal isModalOpen={addressFlag} handleClose={handleClose} handleSubmit={handleSubmit} searchAddress={searchAddress} />
                <label htmlFor="addressInput" className="hidden-label">address</label>

                <Row gutter={[24, 24]} className="address-row">
                  <Col xs={24} md={6} className="service-details-col">
                    <h3 className="service-address-title">{t('serviceability.serviceAddress')}</h3>
                    <div className="service-address-text">
                      <p>{normalizedAddress?.address1}</p>
                      {normalizedAddress?.address2 && <p>{normalizedAddress?.address2}</p>}
                      <p>{normalizedAddress?.city}, {normalizedAddress?.state} {normalizedAddress?.zipCode}</p>
                    </div>
                  </Col>

                  <Col xs={24} md={18}>
                    <div className="serv-input-wrapper">
                      <AddressSuggestionSelect
                        autoFocus={true}
                        loading={loadingState}
                        setSearchAddress={setAddress}
                        className="userInput"
                        parentComp="servCheck"
                        placeholder={t('serviceability.placeholder')}
                        val={currentDisplayAddress}
                      />
                      <div className="serv-update-btn-wrapper">
                        {
                          loading ? <Loader /> :
                            <button
                              type="submit"
                              className="update-btn"
                              onClick={() => handleSubmit(searchAddress, false)}
                              disabled={!validAddr || (!internetAddress && !mobileAddress && !businessAddress)}
                            >
                              <div className="btn-color-darkblue">
                                <i className="fas fa-chevron-circle-right" />
                                {t('common.update')}
                              </div>
                            </button>
                        }
                      </div>
                    </div>

                    <div className="addres-labels m-top-1">
                      <div className="d-flex align-center">
                        {
                          sessionResponse?.businessFlag &&
                        <>
                          <i className="fas fa-suitcase"></i>
                          <Checkbox
                            name="businessAddress"
                            onChange={() => { setBusinessAddr(!businessAddress) }}
                            checked={businessAddress}
                          >
                            {t('serviceability.businessAddress')}
                          </Checkbox>
                        </>
                        }
                        <span className="manual-address-link">
                          {sessionResponse?.businessFlag && <span className="dot-separator">&middot;</span>}
                          <button type="button" className="transparent-btn link-text" onClick={() => setShowManualAddressModal(true)}>{t('serviceability.enterManually')}</button>
                        </span>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
            {showManualAddressModal && (
              <EnterManualAddressModal
                onClose={() => setShowManualAddressModal(false)}
                isBusiness={businessAddress}
                setAddress={setAddress}
              />
            )}
          </div>
        </section>
      </>
    );
  }
  return (
    <BackdropLoader />
  )
}

export default React.memo(ServiceabilityCheck);
